import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useFormikContext } from "formik";
import { Stack, Typography, Tooltip, IconButton, styled, TextField, Collapse, Button, CircularProgress, InputAdornment } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react"
import { AddIcon, ArrowDown01Icon, Call02Icon, DeleteIcon } from "@hugeicons/core-free-icons";
import { useFetchDriversQuery } from "@/api/driversApi";
import { Drivers } from "@/types/drivers";
import InputMask from "react-input-mask";
import { sanitizePhone } from "@/utils/phones";


const ExpandMore = styled(IconButton)(({ theme, expand }: { theme: any; expand: boolean }) => ({
    transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
    marginLeft: "auto",
    transition: theme.transitions.create("transform", {
        duration: theme.transitions.duration.shortest,
    }),
}));

const DriversForm = () => {
    const { organizationId } = useParams();
    const [isExpanded, setIsExpanded] = useState(false);
    const { data: drivers, isSuccess, isError } = useFetchDriversQuery(organizationId || "");
    const { values, setFieldValue, errors, touched, handleBlur } = useFormikContext<any>();

    const handleDeleteDriver = async (driverIndex: number) => {
        const draft = [...values.settings.drivers];
        const driver = draft[driverIndex];

        if (driver.isNew) {
            draft.splice(driverIndex, 1);
        } else {
            driver.isDeleted = true;
            delete driver?.isUpdated;
            draft[driverIndex] = driver;
        }

        setFieldValue("settings.drivers", draft);
    };

    const driversList = useMemo(() => {
        return (values.settings.drivers ?? []).filter((driver: Drivers) => !driver.isDeleted);
    }, [values.settings.drivers]);


    useEffect(() => {
        if (isSuccess && drivers) {
            setFieldValue("settings.drivers", drivers.map(d => ({
                ...d,
                isNew: false,
                isUpdated: false,
            })));
        }
    }, [isSuccess, drivers]);

    if (isError) {
        return (<p>Error</p>)
    }

    if (isSuccess) {
        return (
            <>
                <Stack spacing={{ xs: 3, md: 2 }} direction={{ md: "row" }} alignItems={"center"} justifyContent={"space-between"}>
                    <Stack>
                        <Typography variant="h4">Asociados</Typography>
                        <Typography variant="body1" color="text.secondary">
                            Administra los conductores asociados a tu comercio.
                        </Typography>
                    </Stack>
                    <Stack spacing={2} direction={"row"} alignItems={"center"} justifyContent={"flex-end"}>
                        <ExpandMore expand={isExpanded} onClick={() => setIsExpanded(!isExpanded)}>
                            <Tooltip title={isExpanded ? "Contraer" : "Expandir"}>
                                <HugeiconsIcon icon={ArrowDown01Icon} />
                            </Tooltip>
                        </ExpandMore>

                    </Stack>
                </Stack >

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Stack spacing={2}>
                        {driversList.map((driver: Drivers, index: number) => {
                            return (

                                <Stack
                                    key={`${driver.id}-${index}`}
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                >
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={driver?.name}
                                        onChange={(e) => {
                                            const draft = [...values.settings.drivers];
                                            draft[index] = {
                                                ...driver,
                                                name: e.target.value,
                                                isUpdated: true,
                                            };

                                            setFieldValue("settings.drivers", draft);
                                        }}
                                        onBlur={() => handleBlur({ target: { name: `settings.drivers.${index}.name` } })}
                                        error={!!((touched?.settings?.drivers as any)?.[index])?.name && !!((errors?.settings?.drivers as any)?.[index])?.name}
                                        helperText={((errors?.settings?.drivers as any)?.[index])?.name}
                                    />

                                    <InputMask
                                        mask="9999-9999"
                                        placeholder="0000-0000"
                                        alwaysShowMask={false}
                                        value={driver?.phone ?? ""}
                                        onChange={(e) => {
                                            const draft = [...values.settings.drivers];

                                            draft[index] = {
                                                ...driver,
                                                phone: sanitizePhone(e.target.value),
                                                isUpdated: true,
                                            };

                                            setFieldValue("settings.drivers", draft);
                                        }}
                                        onBlur={
                                            () => {
                                                handleBlur({
                                                    target: {
                                                        name: `settings.drivers.${index}.phone`,
                                                        value: driver?.phone,
                                                    },
                                                });
                                            }
                                        }
                                    >
                                        {() => (
                                            <TextField
                                                label="Teléfono"
                                                fullWidth
                                                size="small"
                                                error={!!((errors?.settings?.drivers as any)?.[index])?.phone && !!((touched?.settings?.drivers as any)?.[index])?.phone}
                                                helperText={((errors?.settings?.drivers as any)?.[index])?.phone && ((touched?.settings?.drivers as any)?.[index])?.phone && ((errors?.settings?.drivers as any)?.[index])?.phone}
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <HugeiconsIcon icon={Call02Icon} size={20} />
                                                                <Typography sx={{ ml: 1, fontWeight: 500 }}>+505</Typography>
                                                            </InputAdornment>
                                                        ),
                                                    }
                                                }}
                                            />
                                        )}
                                    </InputMask>

                                    <IconButton
                                        onClick={() => handleDeleteDriver(index)}
                                    >
                                        <HugeiconsIcon icon={DeleteIcon} />
                                    </IconButton>
                                </Stack>
                            );
                        })}
                        <Stack spacing={2}>
                            <Button
                                variant="outlined"
                                startIcon={<HugeiconsIcon icon={AddIcon} />}
                                onClick={() => {
                                    const draft = [...(values.settings.drivers || [])];

                                    draft.push({
                                        name: "",
                                        phone: "",
                                        organizations: [organizationId],
                                        isNew: true,
                                    });

                                    setFieldValue("settings.drivers", draft);
                                }}
                            >
                                Añadir Repartidor
                            </Button>
                        </Stack >
                    </Stack>
                </Collapse >
            </>
        )
    };

    return (
        <Stack justifyContent={"center"} alignItems={"center"} height={"100%"}>
            <CircularProgress />
        </Stack>
    );
};


export default DriversForm;